import { React, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Input,
} from "reactstrap";

import FormHelpers from "../helpers/FormHelpers";

import CardPreview from "./Cards/CardPreview";
import EcardPreview from "./Ecards/EcardPreview";
import Card from "./Cards/Card";
import Verse from "./Cards/Verse";

import { cardPreviewActions } from "../store/card-preview-slice";

function PreviewArea() {
  const dispatch = useDispatch();

  // overall preview state
  const showCardGallery = useSelector(
    (state) => state.cardPreview.ShowCardGallery
  );
  const showVerseGallery = useSelector(
    (state) => state.cardPreview.ShowVerseGallery
  );
  const showCustomVerseInput = useSelector(
    (state) => state.cardPreview.ShowCustomVerseInput
  );
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );

  const currentPreviewState = useSelector((state) => state.cardPreview);
  const formStep = useSelector((state) => state.configurator.FormStep);

  const requestDetails = currentRequest.DonorRequest;
  const currentOccasion = currentPreviewState.Occasion;
  const occasionLoading = currentPreviewState.OccasionLoading;
  const isGregorian = requestDetails.MassTypeValue == "gregorian";
  const isEcardPreview = (formStep === 'Approve' && requestDetails.CardTypeValue === 'ecard');

  const [customVerseInput, setCustomVerseInput] = useState(currentPreviewState.CustomVerse);
  const [verseValidationMessage, setVerseValidationMessage] = useState(false);

  // zooming and dragging
  const defaultZoom = 0.6;
  const [zoom, setZoom] = useState(defaultZoom);

  const handleZoom = (zoomIn) => {
    setZoom(zoomIn ? zoom + 0.2 : zoom - 0.2);
  };

  const resetZoom = () => {
    setZoom(defaultZoom);
  };

  // toggle more cards modal
  const toggleCardsModal = (e) => {
    dispatch(cardPreviewActions.setShowCardGallery(!showCardGallery));
  };

  // toggler more verses modal
  const toggleVersesModal = (e) => {
    dispatch(cardPreviewActions.setShowVerseGallery(!showVerseGallery));
  };

  // toggler custom verse modal
  const toggleCustomVerseModal = (e) => {
    dispatch(cardPreviewActions.setShowCustomVerseInput(!showCustomVerseInput));
  };

  const onClickCardHandler = (card) => {
    dispatch(cardPreviewActions.setSelectedCard(card));
    toggleCardsModal();
    resetZoom();
  };

  const onClickVerseHandler = (verse) => {
    setCustomVerseInput('');
    dispatch(cardPreviewActions.setSelectedVerse(verse));
    toggleVersesModal();
    resetZoom();
  };

  const onClickCustomVerseHandler = () => {
    setVerseValidationMessage(false);
    toggleCustomVerseModal();
  };

  const applyCustomVerseHandler = () => {
    const customVerseValid = (customVerseInput.trim() != '' && customVerseInput.length <= 278);

    if(customVerseValid) {
      // set custom verse
      dispatch(cardPreviewActions.setCustomVerse(customVerseInput));

      // hide modals
      dispatch(cardPreviewActions.setShowCustomVerseInput(false));
      dispatch(cardPreviewActions.setShowVerseGallery(false));

      setVerseValidationMessage(false);
    } else {
      setVerseValidationMessage(true);
    }
    
  };

  return (
    <div className="cfg-preview-area-contain">
      <h3>
        <span>Preview</span>
      </h3>
      <div className="cfg-preview-area">
        {occasionLoading && (
          <div className="loading-spinner">
            <Spinner color="primary">Loading...</Spinner>
          </div>
        )}
        {!occasionLoading && (
          <div>
            <div
              className="row cfg-preview-toolbar"
              role="toolbar"
              aria-label="Preview Toolbar"
            >
              <div className="col-6 col-lg-6">
                <button
                  onClick={toggleCardsModal}
                  className="btn btn-primary btn-sm cfg-btn-more-cards"
                >
                  More Cards
                </button>{" "}
                {!isGregorian && (<button
                  onClick={toggleVersesModal}
                  className="btn btn-primary btn-sm cfg-btn-more-verses"
                >
                  More Verses
                </button>)}
              </div>
              {!isEcardPreview && <div className="col-6 col-lg-6 text-end">
                <button
                  onClick={() => handleZoom(true)}
                  className="btn btn-primary btn-sm cfg-btn-zoom-in"
                >
                  <i className="fa fa-search-plus"></i>
                </button>{" "}
                <button
                  onClick={() => handleZoom(false)}
                  className="btn btn-primary btn-sm cfg-btn-zoom-out"
                >
                  <i className="fa fa-search-minus"></i>
                </button>
              </div>}
            </div>

            {!isEcardPreview && <CardPreview zoom={zoom} showCard={true} showEnvelope={true} />}
            {isEcardPreview && <EcardPreview />}

            <Modal
              className="gallery"
              isOpen={showCardGallery}
              toggle={toggleCardsModal}
              size="lg"
            >
              <ModalHeader toggle={toggleCardsModal}>Card Gallery</ModalHeader>
              <ModalBody>
                <Container>
                  <Row>
                    {currentOccasion.CardPreviews.map((card) => (
                      <Card
                        key={card.Id}
                        card={card}
                        onCardClick={onClickCardHandler}
                      />
                    ))}
                  </Row>
                </Container>
              </ModalBody>
            </Modal>

            <Modal
              className="gallery"
              isOpen={showVerseGallery}
              toggle={toggleVersesModal}
              size="lg"
            >
              <ModalHeader toggle={toggleVersesModal}>
                Verse Gallery
              </ModalHeader>
              <ModalBody>
                <Container>
                  <Row>
                    <Col lg="3" className="text-center">
                      <Button
                        className="gallery-img-button"
                        onClick={onClickCustomVerseHandler}
                      >
                        <img
                          src="/dist/images/custom-verse.svg"
                          alt="Compose Your Own Verse"
                        />
                      </Button>
                      <Button
                        onClick={onClickCustomVerseHandler}
                        size="sm"
                        color="primary"
                      >
                        Select
                      </Button>
                    </Col>
                    {currentOccasion.Verses.map((verse) => (
                      <Verse
                        key={verse.Id}
                        verse={verse}
                        onVerseClick={onClickVerseHandler}
                      />
                    ))}
                  </Row>
                </Container>
              </ModalBody>
            </Modal>

            <Modal
              className="custom-verse-input"
              isOpen={showCustomVerseInput}
              toggle={toggleCustomVerseModal}
              size="md"
            >
              <ModalHeader toggle={toggleCustomVerseModal}>
                Add Your Own Verse
              </ModalHeader>
              <ModalBody>
                <Container>
                  <Row className="mb-4">
                    <Col lg="12">
                      <Input
                        name="customverse"
                        type="textarea"
                        id="custom-verse"
                        datamax="269"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        rows="9"
                        cols="30"
                        value={customVerseInput}
                        className="custom-verse"
                        placeholder="9 lines of 30 characters each"
                        onKeyUp={FormHelpers.handleMultilineKeyUp}
                        onKeyDown={FormHelpers.handleMultilineKeyDown}
                        onPaste={FormHelpers.handleMultilineUpdate}
                        onChange={(e) => {
                          FormHelpers.handleMultilineUpdate(e);
                          setCustomVerseInput(e.target.value);
                        }}
                        onBlur={(e) => {
                          FormHelpers.handleMultilineUpdate(e);
                          setCustomVerseInput(e.target.value);
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="12">
                      <Button
                        size="sm"
                        color="primary"
                        onClick={applyCustomVerseHandler}
                      >
                        Apply to Card
                      </Button>{" "}
                      <Button
                        type="button"
                        size="sm"
                        color="primary"
                        onClick={toggleCustomVerseModal}
                      >
                        Cancel
                      </Button>
                      {verseValidationMessage && (<small className="invalid-feedback" style={{ display: 'block', fontSize: '.825em' }}>
                      Please enter a custom verse of up to 9 lines with 30 characters each.
                      </small>)}
                    </Col>
                  </Row>
                </Container>
              </ModalBody>
            </Modal>
          </div>
        )}
      </div>
    </div>
  );
}

export default PreviewArea;
